import classNames from 'classnames';

import { Icon, Typhography } from 'components/core';
import Modal from 'components/core/Modals/Modal/Modal';
import { useEffect, useState } from 'react';
import { CONFIG, getCurrentCustomerId } from 'shared/configs/config';

export interface TextFileModalProps {
  /** @deprecated Prefer achFileId — raw S3 URLs 403 when PRIVATE_S3_PAYMENT_DOCS is on */
  fileURL?: string;
  achFileId?: string;
  fileName?: any;
}

export const TextFileModal = ({ fileURL, achFileId, fileName }: TextFileModalProps) => {
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(fileURL);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onLoad = () => {
    setTextModalOpen(!textModalOpen);
  };

  useEffect(() => {
    if (!textModalOpen) {
      return;
    }

    if (!achFileId) {
      setResolvedUrl(fileURL);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${CONFIG.API_TOKEN}`,
      Accept: 'application/json',
    };
    const customerId = getCurrentCustomerId();
    if (customerId) {
      headers['x-customer-id'] = customerId;
    }

    setIsLoading(true);
    setLoadError(null);
    setResolvedUrl(undefined);

    fetch(`${CONFIG.BASE_URL}/customer/ach-files/${achFileId}/download`, {
      method: 'GET',
      headers,
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Unable to open file (${res.status})`);
        }
        const body = (await res.json()) as { url?: string };
        if (!body?.url) {
          throw new Error('Unable to open file (missing url)');
        }
        if (!cancelled) {
          setResolvedUrl(body.url);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setLoadError(err.message || 'Unable to open file');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [textModalOpen, achFileId, fileURL]);

  return (
    <>
      <span onClick={onLoad} className={'underline cursor-pointer text-inherit'}>
        {fileName.substring(0, 15)}
      </span>
      <Modal
        isOpen={textModalOpen}
        onClose={() => setTextModalOpen(false)}
        modalAdditionalClasses={classNames([
          'min-h-[100px] pt-7 pb-8 rounded-lg bg-white flex-col !w-[600px] px-6',
        ])}
      >
        <Typhography component="p" type="h2" color="text-grey-900 mb-6">
          {fileName}
        </Typhography>
        <div
          className="absolute cursor-pointer top-7 right-7 text-grey-600 "
          onClick={() => setTextModalOpen(false)}
        >
          <Icon iconColor="inherit" iconType="CloseIcon" />
        </div>
        <div className="relative flex w-full h-auto">
          {isLoading ? (
            <Typhography component="p" type="h1" color="text-grey-600">
              Loading file…
            </Typhography>
          ) : null}
          {loadError ? (
            <Typhography component="p" type="h1" color="text-red-600">
              {loadError}
            </Typhography>
          ) : null}
          {!isLoading && !loadError && resolvedUrl ? (
            <iframe
              width="100%"
              height={'350px'}
              title="textFilemodal"
              id="textFilemodal"
              src={resolvedUrl}
              className="relative flex w-full overflow-y-auto"
            ></iframe>
          ) : null}
        </div>
      </Modal>
    </>
  );
};
